import { SortDirection } from '../../api';

/** Represents the state parameters used to narrow down the search results. */
export type SearchParameters = {
  /** The scope of the search. */
  scope: 'conversations' | 'users' | 'companies';
  /** The query or keyword. */
  query?: string;
  /** The configuration of filters. */
  filters?: SearchFilters;
  /** The sort object. */
  sort?: SearchSort;
  /** The exact match boolean. */
  strict?: boolean;
  /** The page to display. */
  page?: number;
  /** The number of hits per page. */
  page_size?: number;
  /** The fields to enrich in the response. */
  enrichmentSpec?: string[];
};

/**
 * ## Represents the fields that you can sort by.
 *
 * Options:
 *
 * - `updated_at` — The date when the conversation was updated.
 * - `created_at` — The date when the conversation was created.
 * - `last_message_created_at` — The date when the last message was added to the
 *   conversation.
 */
export type SearchSortField =
  | 'updated_at'
  | 'created_at'
  | 'last_message_created_at'
  | 'display_name';

/** The sort object. */
export type SearchSort = {
  /** The field we are sorting by. */
  field: SearchSortField;
  /** The sorting direction. */
  order: SortDirection;
};

/** The filters object. */
export type SearchFilters = {
  /** The filtering conditions to be applied. */
  conditions: SearchFilterCondition[];
  /** The global filtering strategy to be applied. */
  strategy: SearchFilterStrategy;
};

/**
 * ## Represents the strategy used for filtering.
 *
 * Options:
 *
 * - `all` — All filters must match.
 * - `any` — At least one of the filters must match.
 */
export type SearchFilterStrategy = 'all' | 'any';

/** Represents the filtering condition. */
export type SearchFilterCondition =
  | SearchFilterConditionOneToManyAndNullish
  | SearchFilterConditionOneToOneAndNullish
  | SearchFilterConditionOneToOne
  | SearchFilterConditionDuration
  | SearchFilterConditionText
  | SearchFilterConditionDate
  | SearchFilterConditionCustomAttribute
  | SearchFilterConditionBoolean;

export type SearchFilterConditionOneToManyAndNullish =
  SearchFilterConditionBase<
    /** Conversation */
    | 'tags'
    /** Contact */
    | 'teams'
    | 'roles',
    SearchFilterValueString[],
    SearchFilterOperatorOneToMany | SearchFilterOperatorNullish
  >;

export type SearchFilterConditionOneToOneAndNullish = SearchFilterConditionBase<
  /** Conversation */
  | 'agent_id'
  | 'queue_id'
  /** Contact */
  | 'company_id'
  | 'teams'
  | 'roles',
  SearchFilterValueString[],
  SearchFilterOperatorOneToOne | SearchFilterOperatorNullish
>;

export type SearchFilterConditionOneToOne = SearchFilterConditionBase<
  /** Conversation */
  | 'numeric_id'
  | 'initial_direction'
  | 'requested_by'
  | 'status'
  | 'initial_channel'
  | 'message_author_id'
  | 'note_author_id'
  /** Contact */
  | 'id'
  | 'user_type',
  SearchFilterValueString[],
  SearchFilterOperatorOneToOne
>;

export type SearchFilterConditionDuration = SearchFilterConditionBase<
  'duration',
  SearchFilterValueString[],
  SearchFilterOperatorNumber,
  {
    meta?: {
      initial_request: string;
    };
  }
>;

export type SearchFilterConditionDate = SearchFilterConditionBase<
  | 'created_at'
  | 'assigned_at'
  | 'closed_at'
  | 'last_message_created_at'
  | 'updated_at',
  SearchFilterValueString[],
  SearchFilterOperatorDate,
  {
    meta?: {
      timezone: string;
    };
  }
>;

export type SearchFilterConditionText = SearchFilterConditionBase<
  'note_message',
  SearchFilterValueString[],
  SearchFilterOperatorText
>;

export type SearchFilterConditionCustomAttributeField = `${
  | 'conversation'
  | 'contact'}_custom_attribute`;

export type SearchFilterConditionCustomAttribute =
  | SearchFilterConditionCustomAttributeSelect
  | SearchFilterConditionCustomAttributeText;

export type SearchFilterConditionCustomAttributeSelect =
  SearchFilterConditionBase<
    SearchFilterConditionCustomAttributeField,
    SearchFilterValueStringList[],
    SearchFilterOperatorOneToOne | SearchFilterOperatorNullish,
    {
      /** The unique id of the custom attribute. */
      attribute_id: string;
    }
  >;

export type SearchFilterConditionCustomAttributeText =
  SearchFilterConditionBase<
    SearchFilterConditionCustomAttributeField,
    SearchFilterValueString[],
    SearchFilterOperatorOneToOne | SearchFilterOperatorNullish,
    {
      /** The unique id of the custom attribute. */
      attribute_id: string;
    }
  >;

export type SearchFilterConditionCustomAttributeSelectValue =
  SearchFilterConditionCustomAttributeSelect['values'][number]['value'];

export type SearchFilterConditionCustomAttributeTextValue =
  SearchFilterConditionCustomAttributeText['values'][number]['value'];

export type SearchFilterConditionBoolean = SearchFilterConditionBase<
  /** Contact */
  'is_working' | 'is_online' | 'has_acw' | 'is_assigned',
  SearchFilterValueBoolean[],
  SearchFilterOperatorBoolean
>;

export type SearchFilterConditionBase<
  TConditionField extends string,
  TConditionValues extends unknown[],
  TConditionOperator extends SearchFilterOperator,
  TConditionData extends Record<string, unknown> = never,
> = {
  /** The field this filter applies to. */
  field: TConditionField;
  /** The operator being used by this filter. */
  operator: TConditionOperator;
  /** The values used by this filter. */
  values: TConditionValues;
} & ([TConditionData] extends [never] ? unknown : TConditionData);

/**
 * ## Represents the conversation or contact fields that can be filtered on.
 *
 * Conversation fields:
 *
 * - `numeric_id` — The numeric id of the conversation.
 * - `tags` — The tags applied to the conversation.
 * - `queue_id` — The queue the conversation is in.
 * - `agent_id` — The agent assigned to the conversation.
 * - `requested_by` — The end user.
 * - `status` — The status of the conversation.
 * - `initial_channel` — The channel of the conversation.
 * - `initial_direction` — The direction of the conversation.
 * - `duration` — The time from when the conversation was created until it was
 *   closed.
 * - `note_message` — The internal note added to the conversation.
 * - `message_author_id` — The author of a message in the conversation.
 * - `note_author_id` — The author of an internal note in the conversation.
 * - `created_at` — The date when the conversation was created.
 * - `updated_at` — The date when the conversation was last updated.
 * - `last_message_created_at` — The date when the last message was added to the
 *   conversation.
 * - `assigned_at` — The date when the conversation was assigned to an agent.
 * - `closed_at` — The date when the conversation was closed.
 * - `conversation_custom_attribute` — The custom attribute of the conversation.
 *
 * Contact fields: *
 *
 * - `id` — The unique id of the contact.
 * - `user_type` — The type of the user.
 * - `company_id` — The company id of the contact.
 * - `teams` — The teams the contact belongs to.
 * - `contact_custom_attribute` — The custom attribute of the contact.
 */
export type SearchFilterField = SearchFilterCondition['field'];

/** Represents the filter value base type. */
type SearchFilterValueBase<TValueType> = {
  /** The display name of the value. */
  label: string;
  /** The value being filtered. */
  value: TValueType;
};

export type SearchFilterValueString = SearchFilterValueBase<string>;
export type SearchFilterValueStringList = SearchFilterValueBase<string[]>;
export type SearchFilterValueBoolean = SearchFilterValueBase<'true' | 'false'>;

/** Represents a value used by a filter. */
export type SearchFilterValue = SearchFilterCondition['values'][number];

/** Represents the operators used for filtering one to many relations. */
export type SearchFilterOperatorOneToMany =
  | 'includes_one'
  | 'excludes_one'
  | 'includes_all'
  | 'excludes_all';

/** Represents the operators used for filtering one to one relations. */
export type SearchFilterOperatorOneToOne = 'is_one' | 'is_not_one';

/** Represents the operators used for filtering nullish relations. */
export type SearchFilterOperatorNullish = 'is_empty' | 'is_not_empty';

/** Represents the operators used for filtering numeric relations. */
export type SearchFilterOperatorNumber = 'between' | 'gte' | 'lte';

/** Represents the operators used for filtering text fields. */
export type SearchFilterOperatorText = 'contains' | 'not_contains';

/** Represents the operators used for filtering time periods. */
export type SearchFilterOperatorDate = 'between' | 'since' | 'until';

/** Represents the operators used for filtering boolean fields. */
export type SearchFilterOperatorBoolean = 'equals' | 'not_equals';

/**
 * ## Represents a search filter operator.
 *
 * ### One to many options:
 *
 * Represents the operators used for filtering one to many relations.
 *
 * - `includes_one` — The array field contains at least one of the chosen values.
 * - `excludes_one` — The array field does not contain at least one of the chosen
 *   values.
 * - `includes_all` — The array field contains all of the chosen values, and
 *   possibly others.
 * - `excludes_all` — The array field does not contain all of the chosen values,
 *   but can contain others.
 *
 * ### One to one options:
 *
 * Represents the operators used for filtering one to one relations.
 *
 * - `is_one` — The string field is one of the chosen values.
 * - `is_not_one` — The string field is not one of the chosen values.
 *
 * ### Nullish options:
 *
 * Represents the operators used for filtering nullish relations.
 *
 * - `is_empty` — The field has no value set.
 * - `is_not_empty` — The field has any value set.
 *
 * ### Number options:
 *
 * Represents the operators used for filtering numeric fields.
 *
 * - `between` — The number field is between the chosen range value.
 * - `gte` — The number field is higher than or equal to the chosen value.
 * - `lte` — The number field is lower than or equal to the chosen value.
 *
 * ### Text options:
 *
 * Represents the operators used for filtering text fields.
 *
 * - `contains` — The text field contains the chosen substring.
 * - `not_contains` — The text field does not contain the chosen substring.
 *
 * ### Date options:
 *
 * Represents the operators used for filtering time periods.
 *
 * - `between` — The date field is between the chosen range value.
 * - `since` — The date field is after the chosen value (including).
 * - `until` — The date field is before the chosen value (including).
 */
export type SearchFilterOperator = SearchFilterCondition['operator'];

/** Represents a list of date range preset keys used for quick date selection. */
export const searchFilterDateRangePresetKeys = [
  'today',
  'yesterday',
  'last_7_days',
  'last_4_weeks',
  'last_3_months',
] as const;

/** Represents a type mapped from the list of date range preset keys. */
export type SearchFilterDateRangePresetKey =
  (typeof searchFilterDateRangePresetKeys)[number];

/** Represents a duration object with a number value and duration unit. */
export type SearchDuration = {
  /** The duration value corresponding to the unit. */
  value: number;
  /** The duration unit. */
  unit: SearchDurationUnit;
};

/** Represents a duration unit. */
export type SearchDurationUnit = 'seconds' | 'minutes' | 'hours';
